import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import cn from '@/utils/cn';
import SPASectionHeading from '../../shared/SPASectionHeading';
import { useSPAToast } from '../../shared/SPAToast';
import { PanelFooter, hintClass, panelClass } from '../../shared/formPanel';
import {
  Divider, IMAGE_LABELS, IMAGES_HINT, ImageRow, UPLOADABLE,
} from './ProgramImageRow';
import {
  EditableProgram, ProgramImageKind, ProgramImageUploads, useUploadProgramImages,
} from './useProgram';

const ProgramImagesPanel = () => {
  const program = useOutletContext<EditableProgram>();
  const spaToast = useSPAToast();
  const upload = useUploadProgramImages(program.id);
  const [pending, setPending] = useState<ProgramImageUploads>({});
  const uploadable = program.images.filter(image => UPLOADABLE.includes(image.kind));
  const shownOnly = program.images.filter(image => !UPLOADABLE.includes(image.kind) && image.file_name);
  const hasPending = Object.values(pending).some(Boolean);

  const pick = (kind: ProgramImageKind) => (file: File | undefined) => (
    setPending(current => ({ ...current, [kind]: file }))
  );

  const save = (event: React.FormEvent) => {
    event.preventDefault();
    if (!hasPending) return;

    upload.mutate(pending, {
      onSuccess: () => {
        setPending({});
        spaToast.success('Saved program images.');
      },
    });
  };

  return (
    <form noValidate className={panelClass} onSubmit={save}>
      <SPASectionHeading className="tw-mb-3" title="Program Images" />
      <span className={cn(hintClass, 'tw-mb-3 tw-mt-0')}>{IMAGES_HINT}</span>

      <div className="tw-flex tw-flex-col">
        {uploadable.map((image, index) => (
          <React.Fragment key={image.kind}>
            {index > 0 && <Divider />}
            <ImageRow
              image={image}
              inputId={`program_${image.kind}`}
              label={IMAGE_LABELS[image.kind]}
              pending={pending[image.kind]}
              onPick={pick(image.kind)}
            />
          </React.Fragment>
        ))}
      </div>

      {shownOnly.length > 0 && (
        <div className="tw-mt-5 tw-flex tw-flex-col">
          <SPASectionHeading className="tw-mb-1" title="Other Images" />
          {shownOnly.map((image, index) => (
            <React.Fragment key={image.kind}>
              {index > 0 && <Divider />}
              <ImageRow image={image} inputId={`program_${image.kind}`} label={IMAGE_LABELS[image.kind]} />
            </React.Fragment>
          ))}
        </div>
      )}

      <PanelFooter disabled={!hasPending} update={upload} />
    </form>
  );
};

export default ProgramImagesPanel;
